/**
 * Predictions Tab - View original/modified predictions, changes, and raw lesson text
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { LessonV1_SpineTagsPrediction, TagCandidate, LimiterGroup } from '../../types';
import { predictionsApi, JobStatusResponse } from '../../services/predictions.service';
import { useAppStore } from '../../store/appStore';
import { useNotificationStore } from '../../store/notificationStore';

interface PredictionTabProps {
  prediction: LessonV1_SpineTagsPrediction;
}

// Summary component for prediction data
function PredictionSummary({ tagCandidates, title }: { tagCandidates: TagCandidate[] | undefined; title: string }) {
  if (!tagCandidates || tagCandidates.length === 0) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography color="text.secondary">No {title.toLowerCase()} data available</Typography>
      </Paper>
    );
  }

  const totalCompetencies = tagCandidates.length;
  const selectedCompetencies = tagCandidates.filter(tc => tc.competency?.selected);

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>{title} Summary</Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Chip label={`Total Competencies: ${totalCompetencies}`} />
        <Chip label={`Selected: ${selectedCompetencies.length}`} color="primary" />
      </Box>

      {selectedCompetencies.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Selected Competencies:</Typography>
          {selectedCompetencies.map((tc, idx) => (
            <Accordion key={tc.competency?.spine_node_id || idx} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {tc.competency?.description || 'Unknown Competency'}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${tc.limiter_groups?.filter(lg => lg.selected).length || 0} limiter groups`}
                    sx={{ ml: 'auto' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <SelectedLimiterGroups limiterGroups={tc.limiter_groups} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Paper>
  );
}

// Component to show selected limiter groups and their limiters
function SelectedLimiterGroups({ limiterGroups }: { limiterGroups: LimiterGroup[] | undefined }) {
  if (!limiterGroups) return null;

  const selectedGroups = limiterGroups.filter(lg => lg.selected);

  if (selectedGroups.length === 0) {
    return <Typography variant="body2" color="text.secondary">No selected limiter groups</Typography>;
  }

  return (
    <Box sx={{ pl: 2 }}>
      {selectedGroups.map((lg, idx) => (
        <Box key={lg.limiter_group_id || idx} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {lg.description || lg.limiter_group_id}
          </Typography>
          <Box sx={{ pl: 2, mt: 0.5 }}>
            {lg.limiters?.filter(l => l.selected).map((limiter, lIdx) => (
              <Typography key={limiter.limiter_id || lIdx} variant="caption" display="block" color="text.secondary">
                • {limiter.description || limiter.limiter_id}
              </Typography>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// JSON viewer component
function JsonViewer({ data, title }: { data: any; title: string }) {
  if (!data) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography color="text.secondary">No {title.toLowerCase()} data available</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Raw JSON:</Typography>
      <Box
        component="pre"
        sx={{
          bgcolor: 'grey.900',
          color: 'grey.100',
          p: 2,
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: '500px',
          fontSize: '0.75rem',
          fontFamily: 'monospace',
        }}
      >
        {JSON.stringify(data, null, 2)}
      </Box>
    </Paper>
  );
}

// Competency list accordion for changes
function CompetencyChangeList({
  tagCandidates,
  title: _title,
  color
}: {
  tagCandidates: TagCandidate[];
  title: string;
  color: 'success' | 'error' | 'info';
}) {
  if (tagCandidates.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ pl: 2, py: 1 }}>
        None
      </Typography>
    );
  }

  const colorMap = {
    success: 'success.main',
    error: 'error.main',
    info: 'info.main',
  };

  return (
    <Box>
      {tagCandidates.map((tc, idx) => (
        <Accordion key={tc.competency?.spine_node_id || idx} sx={{ mb: 0.5 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colorMap[color] }} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {tc.competency?.description || tc.competency?.spine_node_id || 'Unknown'}
              </Typography>
              <Chip
                size="small"
                label={`${tc.limiter_groups?.filter(lg => lg.selected).length || 0} groups`}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <SelectedLimiterGroups limiterGroups={tc.limiter_groups} />
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

// Changes Summary component
function ChangesSummary({ original, modified }: { original: TagCandidate[] | undefined; modified: TagCandidate[] | undefined }) {
  if (!original && !modified) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography color="text.secondary">No prediction data available to compare</Typography>
      </Paper>
    );
  }

  // Get selected competencies from each
  const originalSelected = original?.filter(tc => tc.competency?.selected) || [];
  const modifiedSelected = modified?.filter(tc => tc.competency?.selected) || [];

  // Get spine_node_ids for comparison
  const originalIds = new Set(originalSelected.map(tc => tc.competency?.spine_node_id));
  const modifiedIds = new Set(modifiedSelected.map(tc => tc.competency?.spine_node_id));

  // Categorize competencies
  const inCommon = modifiedSelected.filter(tc => originalIds.has(tc.competency?.spine_node_id));
  const addedInModified = modifiedSelected.filter(tc => !originalIds.has(tc.competency?.spine_node_id));
  const removedFromOriginal = originalSelected.filter(tc => !modifiedIds.has(tc.competency?.spine_node_id));

  // Count limiter groups
  const originalLimiterGroups = original?.reduce((sum, tc) => sum + (tc.limiter_groups?.filter(lg => lg.selected).length || 0), 0) || 0;
  const modifiedLimiterGroups = modified?.reduce((sum, tc) => sum + (tc.limiter_groups?.filter(lg => lg.selected).length || 0), 0) || 0;

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Changes Summary</Typography>

      {/* Key Metrics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Metric</Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Original</Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Modified</Typography>
        </Box>

        <Typography>Selected Competencies</Typography>
        <Typography>{originalSelected.length}</Typography>
        <Typography sx={{ color: modifiedSelected.length !== originalSelected.length ? 'warning.main' : 'inherit' }}>
          {modifiedSelected.length} {modifiedSelected.length !== originalSelected.length && `(${modifiedSelected.length > originalSelected.length ? '+' : ''}${modifiedSelected.length - originalSelected.length})`}
        </Typography>

        <Typography>Selected Limiter Groups</Typography>
        <Typography>{originalLimiterGroups}</Typography>
        <Typography sx={{ color: modifiedLimiterGroups !== originalLimiterGroups ? 'warning.main' : 'inherit' }}>
          {modifiedLimiterGroups} {modifiedLimiterGroups !== originalLimiterGroups && `(${modifiedLimiterGroups > originalLimiterGroups ? '+' : ''}${modifiedLimiterGroups - originalLimiterGroups})`}
        </Typography>
      </Box>

      {/* Competency Changes */}
      {!modified ? (
        <Typography color="text.secondary">
          No modifications have been made yet.
        </Typography>
      ) : (
        <Box>
          {/* In Common */}
          <Accordion defaultExpanded={inCommon.length > 0 && inCommon.length <= 5}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1">In Common</Typography>
                <Chip size="small" label={inCommon.length} color="info" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <CompetencyChangeList tagCandidates={inCommon} title="In Common" color="info" />
            </AccordionDetails>
          </Accordion>

          {/* Added in Modified */}
          <Accordion defaultExpanded={addedInModified.length > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1">Added in Modified</Typography>
                <Chip size="small" label={addedInModified.length} color="success" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <CompetencyChangeList tagCandidates={addedInModified} title="Added" color="success" />
            </AccordionDetails>
          </Accordion>

          {/* Removed from Original */}
          <Accordion defaultExpanded={removedFromOriginal.length > 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1">Removed from Original</Typography>
                <Chip size="small" label={removedFromOriginal.length} color="error" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <CompetencyChangeList tagCandidates={removedFromOriginal} title="Removed" color="error" />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Paper>
  );
}

// Re-Run Prediction component
function ReRunPrediction({ prediction }: { prediction: LessonV1_SpineTagsPrediction }) {
  const { selectedProduct, selectedExperiment } = useAppStore();
  const { showNotification } = useNotificationStore();
  const queryClient = useQueryClient();

  // Job state
  const [_jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null);
  const [pollCount, setPollCount] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingError, setPollingError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const startTimeRef = useRef<number | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Check if raw lesson text exists
  const { data: rawTextExists, isLoading: isCheckingRawText } = useQuery({
    queryKey: ['rawLessonTextCheck', selectedProduct, selectedExperiment, prediction.id],
    queryFn: async () => {
      try {
        await predictionsApi.getRawLessonText(selectedProduct, selectedExperiment, prediction.id);
        return true;
      } catch {
        return false;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    };
  }, []);

  // Poll for job status
  const pollJobStatus = useCallback(async (currentJobId: string) => {
    try {
      const status = await predictionsApi.getJobStatus(prediction.id, currentJobId);
      setJobStatus(status);
      setPollCount(prev => prev + 1);
      setPollingError(null);

      // Check if job is complete
      if (status.status === 'SUCCEEDED' || status.status === 'FAILED') {
        // Stop polling
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
        setIsPolling(false);

        if (status.status === 'SUCCEEDED') {
          showNotification('success', 'Tag prediction completed! Switch to the Tags tab to review the results.');
          // Invalidate prediction cache so other tabs refresh
          queryClient.invalidateQueries({ queryKey: ['prediction', selectedProduct, selectedExperiment, prediction.id] });
        } else {
          showNotification('error', `Tag prediction failed: ${status.message}`);
        }
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Unknown error';
      setPollingError(message);
      // Stop polling on error
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
      setIsPolling(false);
      showNotification('error', `Error checking job status: ${message}`);
    }
  }, [prediction.id, selectedProduct, selectedExperiment, queryClient, showNotification]);

  // Start async prediction mutation
  const startMutation = useMutation({
    mutationFn: () => predictionsApi.startAsyncPrediction(
      selectedProduct,
      selectedExperiment,
      prediction.id
    ),
    onSuccess: (data) => {
      setJobId(data.job_id);
      setJobStatus({ job_id: data.job_id, status: 'INIT', message: data.message, details: {}, started_at: new Date().toISOString(), completed_at: null, s3_key: null });
      setPollCount(0);
      setElapsedTime(0);
      setPollingError(null);
      setIsPolling(true);
      startTimeRef.current = Date.now();

      // Start elapsed time counter
      elapsedIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }
      }, 1000);

      // Start polling every 5 seconds
      pollIntervalRef.current = setInterval(() => {
        pollJobStatus(data.job_id);
      }, 5000);

      // Do first poll immediately
      pollJobStatus(data.job_id);
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.message || 'Unknown error';
      showNotification('error', `Failed to start tag prediction: ${message}`);
    },
  });

  const handleRerunClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmRerun = () => {
    setShowConfirmDialog(false);
    setJobId(null);
    setJobStatus(null);
    setPollCount(0);
    setElapsedTime(0);
    setPollingError(null);
    startMutation.mutate();
  };

  const handleCancelRerun = () => {
    setShowConfirmDialog(false);
  };

  const canRerun = rawTextExists && !startMutation.isPending && !isPolling;
  const isRunning = startMutation.isPending || isPolling;

  // Format elapsed time as mm:ss
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'INIT': return 'info';
      case 'PROCESSING': return 'warning';
      case 'SUCCEEDED': return 'success';
      case 'FAILED': return 'error';
      default: return 'default';
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Re-Run Tag Prediction</Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Run the AI tag prediction pipeline for this lesson. This process analyzes the lesson content
        and generates tag candidates based on the configured target standards.
      </Typography>

      {/* Raw Text Check */}
      {isCheckingRawText && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Checking lesson prerequisites...
          </Typography>
        </Box>
      )}

      {!isCheckingRawText && !rawTextExists && (
        <Alert severity="error" sx={{ mb: 3, bgcolor: 'error.main', color: 'error.contrastText', '& .MuiAlert-icon': { color: 'error.contrastText' } }}>
          <Typography variant="body2" fontWeight="bold">
            Raw lesson text is required but not found in S3.
          </Typography>
          <Typography variant="body2">
            The lesson PDF must be processed and the raw text uploaded to S3 before running tag prediction.
            Please contact an administrator to upload the lesson data.
          </Typography>
        </Alert>
      )}

      {/* Request Parameters (Read-only) */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
          Request Parameters
        </Typography>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: 150, border: 'none', py: 0.5 }}>Product</TableCell>
              <TableCell sx={{ border: 'none', py: 0.5, fontFamily: 'monospace' }}>{selectedProduct}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', border: 'none', py: 0.5 }}>Experiment</TableCell>
              <TableCell sx={{ border: 'none', py: 0.5, fontFamily: 'monospace' }}>{selectedExperiment}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', border: 'none', py: 0.5 }}>Lesson ID</TableCell>
              <TableCell sx={{ border: 'none', py: 0.5, fontFamily: 'monospace' }}>{prediction.id}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      {/* Run Button */}
      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={handleRerunClick}
        disabled={!canRerun}
        startIcon={isRunning ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
        sx={{ mb: 2 }}
      >
        {isRunning ? 'Running Prediction...' : 'Re-Run Prediction'}
      </Button>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={handleCancelRerun} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Re-Run Prediction</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Re-running the prediction will overwrite existing tag predictions.
          </Alert>
          <Typography variant="body1" gutterBottom>
            This action will:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            <li>
              <Typography variant="body2">
                Generate fresh tag predictions using the AI pipeline
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Overwrite any existing review edits</strong> you have made to the tag candidates
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Reset the tag candidates to the new AI-generated predictions
              </Typography>
            </li>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRerun}>Cancel</Button>
          <Button onClick={handleConfirmRerun} variant="contained" color="warning">
            Re-Run Prediction
          </Button>
        </DialogActions>
      </Dialog>

      {/* Polling Status */}
      {isRunning && jobStatus && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <LinearProgress color={jobStatus.status === 'PROCESSING' ? 'warning' : 'primary'} />
          </Box>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box>
                <Chip
                  label={jobStatus.status}
                  size="small"
                  color={getStatusColor(jobStatus.status) as any}
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Elapsed Time</Typography>
              <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>{formatTime(elapsedTime)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Poll Count</Typography>
              <Typography variant="h6">{pollCount}</Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {jobStatus.message}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Polling every 5 seconds... This typically takes 60 seconds or more.
          </Typography>
        </Paper>
      )}

      {/* Polling Error */}
      {pollingError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Error checking job status
          </Typography>
          <Typography variant="body2">{pollingError}</Typography>
          <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={handleRerunClick}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Success Result */}
      {jobStatus?.status === 'SUCCEEDED' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Tag prediction completed successfully!
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Switch to the <strong>Tags</strong> tab to review the updated predictions.
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" gutterBottom>Results Summary</Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 'none', py: 0.25, width: 180 }}>Tag Candidates</TableCell>
                  <TableCell sx={{ border: 'none', py: 0.25, fontWeight: 'bold' }}>{jobStatus.details.tag_candidates_count ?? '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', py: 0.25 }}>Ranked Tags</TableCell>
                  <TableCell sx={{ border: 'none', py: 0.25, fontWeight: 'bold' }}>{jobStatus.details.ranked_tags_count ?? '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', py: 0.25 }}>Target Standards</TableCell>
                  <TableCell sx={{ border: 'none', py: 0.25, fontWeight: 'bold' }}>{jobStatus.details.target_standard_ids_count ?? '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', py: 0.25 }}>Lesson Text Length</TableCell>
                  <TableCell sx={{ border: 'none', py: 0.25, fontWeight: 'bold' }}>{jobStatus.details.lesson_text_length?.toLocaleString() ?? '-'} chars</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', py: 0.25 }}>Elapsed Time</TableCell>
                  <TableCell sx={{ border: 'none', py: 0.25, fontWeight: 'bold' }}>{formatTime(elapsedTime)}</TableCell>
                </TableRow>
                {jobStatus.s3_key && (
                  <TableRow>
                    <TableCell sx={{ border: 'none', py: 0.25 }}>S3 Key</TableCell>
                    <TableCell sx={{ border: 'none', py: 0.25, fontFamily: 'monospace', fontSize: '0.75rem' }}>{jobStatus.s3_key}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Alert>
      )}

      {/* Failed Result */}
      {jobStatus?.status === 'FAILED' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Tag prediction failed
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {jobStatus.message}
          </Typography>
          <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={handleRerunClick}>
            Retry
          </Button>
        </Alert>
      )}
    </Paper>
  );
}

export default function PredictionTab({ prediction }: PredictionTabProps) {
  const [subTab, setSubTab] = useState(0);
  const { selectedProduct, selectedExperiment } = useAppStore();

  // Fetch raw lesson text
  const { data: rawText, isLoading: isLoadingRawText, error: rawTextError } = useQuery({
    queryKey: ['rawLessonText', selectedProduct, selectedExperiment, prediction.id],
    queryFn: () => predictionsApi.getRawLessonText(selectedProduct, selectedExperiment, prediction.id),
    enabled: subTab === 3, // Only fetch when Raw Lesson Text tab is active
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false, // Don't retry on 404
  });

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Predictions
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={subTab} onChange={(_, newValue) => setSubTab(newValue)}>
          <Tab label="Original Prediction" />
          <Tab label="Modified Prediction" />
          <Tab label="Changes Summary" />
          <Tab label="Raw Lesson Text" />
          <Tab label="Re-Run Prediction" />
        </Tabs>
      </Box>

      <Box>
        {subTab === 0 && (
          <Box>
            <PredictionSummary tagCandidates={prediction.tag_prediction_original} title="Original Prediction" />
            <JsonViewer data={prediction.tag_prediction_original} title="Original Prediction" />
          </Box>
        )}
        {subTab === 1 && (
          <Box>
            <PredictionSummary tagCandidates={prediction.tag_prediction_modified} title="Modified Prediction" />
            <JsonViewer data={prediction.tag_prediction_modified} title="Modified Prediction" />
          </Box>
        )}
        {subTab === 2 && (
          <ChangesSummary
            original={prediction.tag_prediction_original}
            modified={prediction.tag_prediction_modified}
          />
        )}
        {subTab === 3 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Raw Lesson Text</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Extracted text content from the lesson PDF.
            </Typography>
            {isLoadingRawText && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {rawTextError && (
              <Alert severity="warning">
                Raw lesson text not available for this lesson.
              </Alert>
            )}
            {rawText && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  maxHeight: 600,
                  overflow: 'auto',
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {rawText}
              </Paper>
            )}
          </Paper>
        )}
        {subTab === 4 && <ReRunPrediction prediction={prediction} />}
      </Box>
    </Box>
  );
}
